import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Check, Download, Search, X } from 'lucide-react';
import { SinDatos, DataState, Panel, PanelHeader, Badge, Button, Table, Thead } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { exportarRecomendaciones } from '@/lib/exports';
import { FilaRecomendacion } from './FilaRecomendacion';
import { useApproveRecommendationBatch, useRecommendations } from '@/lib/hooks';
import { formatCompactCop, formatNumber } from '@shared/format';
import type { DoNotReorder, Recommendation } from '@shared/types';
import { cn } from '@/lib/cn';

/**
 * Recomendaciones de compra.
 *
 * ⚠️ Esto era una PILA de 310 tarjetas de 240px de alto, una debajo de la otra: revisarlas
 * significaba recorrer unos 74.000px sin poder ordenar, filtrar ni comparar dos, y sin
 * saber nunca cuánto capital llevabas aprobado. Con los 2 productos de la demo se veía
 * perfecta; con el catálogo real la pantalla donde se decide la plata no se podía operar.
 *
 * Ahora es una tabla: se filtra, se ordena, se eligen varias y se aprueban de una.
 *
 * 📌 Pagina de a 50 por el gotcha ya medido en este proyecto (una tabla larga con celdas
 * editables costaba 1.147ms por tecla; con 50 por página, 11,7ms).
 */

type Orden = 'urgencia' | 'costo' | 'cantidad' | 'fecha' | 'cobertura';
const POR_PAGINA = 50;

const PESO_SEVERIDAD: Record<Recommendation['severity'], number> = { critico: 0, alto: 1, medio: 2 };

export default function RecommendationsPage() {
  const recs = useRecommendations();
  const [busqueda, setBusqueda] = useState('');
  // La urgencia vive en la URL para que el Dashboard pueda enlazar "las 310 críticas"
  // directo, con el filtro ya puesto. Mismo criterio que el semáforo.
  const [params, setParams] = useSearchParams();
  const severidad = (params.get('urgencia') ?? 'todas') as 'todas' | Recommendation['severity'];
  const setSeveridad = (v: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (v === 'todas') p.delete('urgencia');
        else p.set('urgencia', v);
        return p;
      },
      { replace: true },
    );
  const [proveedor, setProveedor] = useState('todos');
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [orden, setOrden] = useState<Orden>('urgencia');
  const [pagina, setPagina] = useState(0);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const aprobarLote = useApproveRecommendationBatch();

  const items = recs.data?.items ?? [];

  const proveedores = useMemo(
    () => [...new Set(items.map((r) => r.supplier).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [items],
  );

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = items.filter((r) => {
      if (soloPendientes && r.status === 'aprobado') return false;
      if (severidad !== 'todas' && r.severity !== severidad) return false;
      if (proveedor !== 'todos' && r.supplier !== proveedor) return false;
      if (!q) return true;
      return (
        r.sku.toLowerCase().includes(q) ||
        r.productName.toLowerCase().includes(q) ||
        r.supplier.toLowerCase().includes(q)
      );
    });
    const cmp: Record<Orden, (a: Recommendation, b: Recommendation) => number> = {
      urgencia: (a, b) =>
        PESO_SEVERIDAD[a.severity] - PESO_SEVERIDAD[b.severity] || b.costEstimate - a.costEstimate,
      costo: (a, b) => b.costEstimate - a.costEstimate,
      cantidad: (a, b) => b.qtyRecommended - a.qtyRecommended,
      fecha: (a, b) => a.orderBefore.localeCompare(b.orderBefore),
      cobertura: (a, b) => a.coverageDays - b.coverageDays,
    };
    return [...lista].sort(cmp[orden]);
  }, [items, busqueda, severidad, proveedor, soloPendientes, orden]);

  const pagLimite = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const paginaActual = Math.min(pagina, pagLimite - 1);
  const visibles = filtradas.slice(paginaActual * POR_PAGINA, (paginaActual + 1) * POR_PAGINA);

  // Sólo se puede aprobar lo que sigue sin decidir. Contar sobre todo lo filtrado y no
  // sobre la página evita que "seleccionar todo" mienta sobre lo que va a aprobar.
  const aprobables = useMemo(() => filtradas.filter((r) => r.status !== 'aprobado'), [filtradas]);
  const elegidasValidas = useMemo(
    () => aprobables.filter((r) => elegidas.has(r.id)),
    [aprobables, elegidas],
  );
  const capitalElegido = elegidasValidas.reduce((s, r) => s + r.costEstimate, 0);
  const faltaCosto = elegidasValidas.filter((r) => r.costEstimate <= 0).length;

  // `useCallback` sin dependencias — con el updater funcional no necesita ninguna. Si esto
  // se recreara en cada render, el `memo` de la fila no serviría de nada y las 50 filas se
  // volverían a dibujar con cada tecla del buscador: el problema que este proyecto ya midió.
  const alternar = useCallback(
    (id: string) =>
      setElegidas((prev) => {
        const s = new Set(prev);
        if (s.has(id)) s.delete(id);
        else s.add(id);
        return s;
      }),
    [],
  );

  const todasDeLaPagina = visibles.filter((r) => r.status !== 'aprobado');
  const todaLaPaginaElegida = todasDeLaPagina.length > 0 && todasDeLaPagina.every((r) => elegidas.has(r.id));

  const reiniciarPagina = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPagina(0);
  };

  return (
    <DataState query={recs}>
      {(data) =>
        // Sin motor no hay qué recomendar. Antes esto mostraba 5 recomendaciones sembradas.
        data.items.length === 0 ? (
          <div className="space-y-6">
            <PageHeader title="Recomendaciones de compra" subtitle="Qué comprar, cuánto y cuándo" />
            <SinDatos
              title="No hay nada que comprar ahora mismo"
              detail="Acá aparecen los productos que llegaron a su punto de reorden, con cuánto comprar y por qué. Que esté vacío significa que ninguno lo alcanzó todavía."
              comoLlenarlo="Se calculan con cada corrida del motor. Si esperabas ver algo, mirá en Forecast y Proyecciones cuántos productos tienen proyección."
            />
          </div>
        ) : (
          <div className="space-y-5 pb-24">
            <PageHeader
              actions={
                <Button
                  variant="subtle"
                  icon={<Download className="h-3.5 w-3.5" />}
                  onClick={() => exportarRecomendaciones(filtradas)}
                  title="Baja las que estás viendo, con los filtros aplicados"
                >
                  Exportar
                </Button>
              }
              title="Recomendaciones de compra"
              subtitle={
                // Sumar los costos de productos que NO tienen costo da un total que se lee
                // como completo y no lo es. Si falta alguno, se dice en vez de mostrar el número.
                data.items.some((r) => r.costEstimate <= 0)
                  ? `${formatNumber(data.items.length)} productos para reponer · falta el valor unitario de ${data.items.filter((r) => r.costEstimate <= 0).length}, así que no se puede estimar el capital`
                  : `${formatNumber(data.items.length)} productos para reponer · capital total estimado ${formatCompactCop(data.capital)} COP`
              }
            />

            {/* --- Filtros --------------------------------------------------------- */}
            <Panel>
              <div className="flex flex-col gap-3 p-4 xl:flex-row xl:items-center">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
                  <input
                    value={busqueda}
                    onChange={(e) => reiniciarPagina(setBusqueda)(e.target.value)}
                    placeholder="Buscar por SKU, producto o proveedor…"
                    className="h-9 w-full rounded-lg border border-hairline bg-card-2 pl-9 pr-8 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-ring/40"
                  />
                  {busqueda && (
                    <button
                      onClick={() => reiniciarPagina(setBusqueda)('')}
                      className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded text-ink-faint hover:bg-hover hover:text-ink-strong"
                      aria-label="Limpiar la búsqueda"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    etiqueta="Urgencia"
                    value={severidad}
                    onChange={(v) => reiniciarPagina(setSeveridad)(v)}
                    opciones={[
                      ['todas', `Toda urgencia (${data.items.length})`],
                      ['critico', `Crítico (${data.counts.critico ?? 0})`],
                      ['alto', `Alto (${data.counts.alto ?? 0})`],
                      ['medio', `Medio (${data.counts.medio ?? 0})`],
                    ]}
                  />
                  <Select
                    etiqueta="Proveedor"
                    value={proveedor}
                    onChange={(v) => reiniciarPagina(setProveedor)(v)}
                    opciones={[['todos', 'Todos los proveedores'], ...proveedores.map((p) => [p, p] as [string, string])]}
                  />
                  <Select
                    etiqueta="Ordenar por"
                    value={orden}
                    onChange={(v) => setOrden(v as Orden)}
                    opciones={[
                      ['urgencia', 'Más urgentes primero'],
                      ['costo', 'Mayor costo primero'],
                      ['cantidad', 'Mayor cantidad primero'],
                      ['fecha', 'Fecha límite más próxima'],
                      ['cobertura', 'Menos días de cobertura'],
                    ]}
                  />
                  <label className="flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-hairline bg-card-2 px-3 text-xs text-ink">
                    <input
                      type="checkbox"
                      checked={soloPendientes}
                      onChange={(e) => reiniciarPagina(setSoloPendientes)(e.target.checked)}
                      className="h-3.5 w-3.5 accent-primary"
                    />
                    Ocultar aprobadas
                  </label>
                </div>
              </div>
            </Panel>

            {/* --- Tabla ----------------------------------------------------------- */}
            <Panel>
              <PanelHeader
                title={
                  filtradas.length === data.items.length
                    ? `${formatNumber(filtradas.length)} recomendaciones`
                    : `${formatNumber(filtradas.length)} de ${formatNumber(data.items.length)} recomendaciones`
                }
                subtitle="Tocá una fila para ver de dónde sale la cantidad y ajustarla"
                right={
                  todasDeLaPagina.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        setElegidas((prev) => {
                          const s = new Set(prev);
                          todasDeLaPagina.forEach((r) => (todaLaPaginaElegida ? s.delete(r.id) : s.add(r.id)));
                          return s;
                        })
                      }
                    >
                      {todaLaPaginaElegida ? 'Quitar la selección' : `Elegir las ${todasDeLaPagina.length} de esta página`}
                    </Button>
                  )
                }
              />

              {filtradas.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-ink-muted">
                  Ninguna recomendación coincide con estos filtros.
                </p>
              ) : (
                <>
                  <Table>
                    <Thead
                      columns={[
                        // En un teléfono no entran diez columnas. Se esconden por PRIORIDAD:
                        // producto, cuánto comprar y cuánto cuesta sobreviven a cualquier
                        // ancho, porque son la decisión; el resto va apareciendo con lugar.
                        { key: 'sel', label: '' },
                        { key: 'prod', label: 'Producto' },
                        { key: 'urg', label: 'Urgencia', className: 'hidden md:table-cell' },
                        { key: 'antes', label: 'Ordenar antes', align: 'right', className: 'hidden lg:table-cell' },
                        { key: 'stock', label: 'Stock', align: 'right', className: 'hidden xl:table-cell' },
                        { key: 'cob', label: 'Cobertura', align: 'right', className: 'hidden lg:table-cell' },
                        { key: 'qty', label: 'Comprar', align: 'right' },
                        { key: 'costo', label: 'Costo est.', align: 'right', className: 'hidden sm:table-cell' },
                        { key: 'estado', label: 'Estado', className: 'hidden xl:table-cell' },
                        { key: 'exp', label: '' },
                      ]}
                    />
                    <tbody>
                      {visibles.map((rec) => (
                        <FilaRecomendacion
                          key={rec.id}
                          rec={rec}
                          elegida={elegidas.has(rec.id)}
                          onElegir={alternar}
                        />
                      ))}
                    </tbody>
                  </Table>

                  {pagLimite > 1 && (
                    <div className="flex items-center justify-between gap-3 border-t border-hairline px-5 py-3">
                      <p className="text-xs text-ink-muted">
                        {paginaActual * POR_PAGINA + 1}–{Math.min((paginaActual + 1) * POR_PAGINA, filtradas.length)} de{' '}
                        {formatNumber(filtradas.length)}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" disabled={paginaActual === 0} onClick={() => setPagina(paginaActual - 1)}>
                          Anterior
                        </Button>
                        <span className="text-xs text-ink-muted">
                          {paginaActual + 1} / {pagLimite}
                        </span>
                        <Button
                          variant="outline"
                          disabled={paginaActual >= pagLimite - 1}
                          onClick={() => setPagina(paginaActual + 1)}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Panel>

            {/* --- Los que NO hay que reponer -------------------------------------- */}
            <Panel>
              <PanelHeader
                title="Productos que no hay que reponer"
                subtitle="Tienen cobertura de sobra: el motor los deja afuera a propósito"
              />
              {data.exclusions.length === 0 ? (
                // Antes esto era una caja vacía sin una palabra adentro: ocupaba lugar y
                // no decía si faltaba el dato o si de verdad no había ninguno.
                <p className="px-5 py-8 text-center text-sm text-ink-muted">
                  Ninguno quedó excluido en la última corrida. Acá aparecen los que tienen tanto stock que
                  comprarlos sería inmovilizar plata.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2 p-5">
                  {data.exclusions.map((ex: DoNotReorder) => (
                    <div
                      key={ex.sku}
                      className="inline-flex items-center gap-2 rounded-lg border border-hairline bg-subtle px-3 py-2"
                    >
                      <span className="font-mono text-2xs text-ink-faint">{ex.sku}</span>
                      <span className="text-xs text-ink">{ex.productName}</span>
                      <span className="text-2xs text-ink-muted">
                        <span className="text-info-text">{ex.coverageDays}d</span> cobertura
                      </span>
                      {ex.flagged && <Badge tone="violet">SOBRESTOCK</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* --- Barra de selección ---------------------------------------------
                Fija abajo: con 310 filas, un botón de aprobar al final de la lista es un
                botón que no existe. Muestra el capital ANTES de apretar, porque eso es
                justamente lo que se está decidiendo. */}
            {elegidasValidas.length > 0 && (
              <div className="fixed inset-x-0 bottom-0 z-30 border-t border-hairline bg-panel/95 px-5 py-3 shadow-alta backdrop-blur lg:left-[var(--ancho-lateral,0px)]">
                <div className="mx-auto flex max-w-[1920px] flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                    <p className="text-sm font-semibold text-ink-strong">
                      {formatNumber(elegidasValidas.length)} seleccionada{elegidasValidas.length === 1 ? '' : 's'}
                    </p>
                    <p className="text-sm text-ink-muted">
                      {faltaCosto > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-warning-text">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {faltaCosto} sin valor unitario: el capital no se puede estimar
                        </span>
                      ) : (
                        <>
                          Capital <span className="cifra text-base">{formatCompactCop(capitalElegido)}</span> COP
                        </>
                      )}
                    </p>
                    {aprobarLote.data && (
                      <p className="text-xs text-success-text">
                        {aprobarLote.data.aprobadas} aprobadas
                        {aprobarLote.data.salteadas > 0 && ` · ${aprobarLote.data.salteadas} ya estaban decididas`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => setElegidas(new Set())}>
                      Cancelar
                    </Button>
                    <Button
                      size="md"
                      variant="success"
                      icon={<Check className="h-4 w-4" />}
                      disabled={aprobarLote.isPending}
                      onClick={() => {
                        const ids = elegidasValidas.map((r) => r.id);
                        // Confirmación explícita: cada aprobación crea una orden de compra,
                        // y de a 100 eso no es un clic que se pueda deshacer cómodo.
                        const ok = window.confirm(
                          `Se van a aprobar ${ids.length} recomendaciones y se va a crear una orden de compra por cada una (en estado borrador, no se le manda nada a ningún proveedor).\n\n¿Seguimos?`,
                        );
                        if (!ok) return;
                        aprobarLote.mutate(ids, { onSuccess: () => setElegidas(new Set()) });
                      }}
                    >
                      {aprobarLote.isPending
                        ? 'Aprobando…'
                        : `Aprobar ${formatNumber(elegidasValidas.length)} y crear las órdenes`}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      }
    </DataState>
  );
}

/** Desplegable de filtro con su rótulo arriba, para que ninguno quede sin nombre. */
function Select({
  etiqueta,
  value,
  onChange,
  opciones,
}: {
  etiqueta: string;
  value: string;
  onChange: (v: string) => void;
  opciones: [string, string][];
}) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="sr-only">{etiqueta}</span>
      <select
        aria-label={etiqueta}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-9 max-w-[15rem] rounded-lg border border-hairline bg-card-2 px-2.5 text-xs text-ink',
          'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-ring/40',
        )}
      >
        {opciones.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
